import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/store';
import { getStorage } from '@/store/storage';
import { cleanDiscoveryHistory, ideaKey } from '@/lib/discoveryExperience';

const EMPTY_HISTORY = Object.freeze({});
export default function useDiscoveryExperience(enabled) {
  const userId = useAuthStore(s => s.user?.id);
  const session = useAuthStore(s => s._sessionGeneration);
  const key = userId ? `gab-discovery-history-v1:${userId}` : null;
  const identity = `${key}:${session}`;
  const current = useRef(null);
  const [loaded, setLoaded] = useState({ identity: null, history: {} });
  useEffect(() => {
    const owner = { identity, history: {}, alive: true, ready: false, timer: null };
    current.current = owner;
    const flush = () => {
      if (!key || !owner.ready) return;
      try { Promise.resolve(getStorage().setItem(key, JSON.stringify(cleanDiscoveryHistory(owner.history)))).catch(() => {}); } catch {}
    };
    owner.flush = flush;
    if (enabled && key) Promise.resolve().then(() => getStorage().getItem(key)).then(raw => {
      let parsed = {}; try { parsed = JSON.parse(raw || '{}'); } catch {}
      if (!owner.alive) return;
      owner.history = cleanDiscoveryHistory(parsed); owner.ready = true;
      // Freeze ranking for this visit, so scrolling never shuffles visible cards.
      setLoaded({ identity, history: { ...owner.history } });
    }).catch(() => { if (owner.alive) { owner.ready = true; setLoaded({ identity, history: {} }); } });
    return () => { owner.alive = false; clearTimeout(owner.timer); flush(); };
  }, [identity, enabled, key]);
  const record = useCallback((idea, type = 'seen') => {
    const owner = current.current;
    if (!enabled || !owner?.ready || owner.identity !== identity || !['seen', 'used'].includes(type)) return;
    const id = ideaKey(idea); if (!id) return;
    owner.history[id] = { ...owner.history[id], [type]: Date.now() };
    clearTimeout(owner.timer); owner.timer = setTimeout(owner.flush, 400);
  }, [enabled, identity]);
  return { ready: loaded.identity === identity, history: loaded.identity === identity ? loaded.history : EMPTY_HISTORY, record };
}
